import {connect} from 'react-redux';
import {Component} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import classNames from 'classnames';

import {isMobile} from '-/util/util';
import SessionService from '-/services/SessionService';
import {important, lighten, rem, inputPlaceholder} from '-/styles/utils';

const styles = theme => ({
    root: {
        display: 'table',
        margin: '0 auto',
        marginTop: rem(15),
        '& span, & input[type="tel"]': {
            display: 'inline-block',
            border: 'none',
            padding: 0,
            fontFamily: theme.fontFamily,
            fontSize: rem(31),
            fontWeight: 'normal',
            lineHeight: rem(44),
            letterSpacing: rem(3),
            color: '#aaa',
            backgroundColor: 'transparent',
            verticalAlign: 'middle',
            ...inputPlaceholder({
                color: lighten('#aaa', .2),
                fontSize: rem(31)
            }),
            '&:focus': {
                outline: 'none',
                border: 'none',
                boxShadow: 'none'
            }
        },
        '& input': {
            color: important('#333')
        },
        '& > div': {
            whiteSpace: 'nowrap'
        }
    },
    input: {
        display: 'inline-block',
        border: 'none',
        fontFamily: '$font-family',
        fontSize: rem(31),
        fontWeight: 'normal',
        lineHeight: rem(44),
        letterSpacing: rem(3),
        color: '#aaa',
        backgroundColor: 'transparent',
        verticalAlign: 'middle',
        ...inputPlaceholder({
            color: lighten('#aaa', .2),
            fontSize: rem(31)
        }),
        '&:focus, &:active': {
            outline: 'none',
            border: 'none',
            boxShadow: 'none'
        }
    },
    last4: {
        width: important(100)
    },
    first41: {
        width: important(66)
    },
    first42: {
        width: important(25)
    },
    first2: {
        width: important(44)
    },
    full1: {
        width: important(76)
    },
    full2: {
        width: important(65)
    },
    full3: {
        width: important(90)
    }
});

class SSNInput extends Component {
    static defaultProps = {
        part: 'full', // || last4 || first4
        onChange: () => {}
    };

    static propTypes = {
        part: PropTypes.string,
        onChange: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.state = {
            first: '',
            middle: '',
            last: ''
        };

        this.onChange = this.onChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleInputFirst4_1Change = this.handleInputFirst4_1Change.bind(this);
        this.handleInputFull_1Change = this.handleInputFull_1Change.bind(this);
        this.handleInputFull_2Change = this.handleInputFull_2Change.bind(this);
    }

    getErrors() {
        const {part} = this.props;
        const value = this.value;

        if (!value) {
            return ['SSN is empty'];
        }

        if (part === 'full' && value.length !== 9 ||
            (part === 'first4' || part === 'last4') && value.length !== 4) {
            return ['SSN is invalid'];
        }

        return [];
    }

    get value() {
        const {inputFirst2, inputLast4, inputFirst4_1, inputFirst4_2, inputFull_1, inputFull_2, inputFull_3} = this.refs;
        const {part} = this.props;

        switch (part) {
            case 'first2':
                return inputFirst2.value;
            case 'last4':
                return inputLast4.value;
            case 'first4':
                return inputFirst4_1.value + inputFirst4_2.value;
            case 'full':
                return inputFull_1.value  + inputFull_2.value + inputFull_3.value;
        }
    }

    handleClick() {
        this.focus();
    }

    componentDidMount() {
        if (!isMobile()) {
            this.focus();
        }

        const {value} = this.props;
        this.setValue(value);
    }

    componentWillReceiveProps(nextProps) {
        const {value} = this.props;

        if (nextProps.value !== value) {
            this.setValue(nextProps.value);
        }
    }

    setValue(value = '') {
        let first = '', middle = '', last = '';
        const {part} = this.props;

        switch (part) {
            case 'first2':
                first = value.substr(0, 2);
                break;
            case 'last4':
                first = value.substr(0, 4);
                break;
            case 'first4':
                first = value.substr(0, 3);
                middle = value.substr(3, 1);
                break;
            case 'full':
                first = value.substr(0, 3);
                middle = value.substr(3, 2);
                last = value.substr(5, 4);
                break;
        }

        this.setState({first, middle, last});
    }

    focus() {
        const {part} = this.props;
        switch (part) {
            case 'last4':
                this.refs.inputLast4.select();
                this.refs.inputLast4.focus();
                break;
            case 'first4':
                this.refs.inputFirst4_1.select();
                this.refs.inputFirst4_1.focus();
                break;
            case 'full':
                this.refs.inputFull_1.select();
                this.refs.inputFull_1.focus();
                break;
        }
    }

    onChange() {
        const {onChange} = this.props;
        const value = this.value.replace(/[^0-9]/gi, '');
        this.setValue(value);
        onChange(value);
    }

    handleInputFirst4_1Change(e) {
        if (e.target.value.length === 3) {
            this.refs.inputFirst4_2.select();
            this.refs.inputFirst4_2.focus();
        }
        this.onChange();
    }

    handleInputFull_1Change(e) {
        if (e.target.value.length === 3) {
            this.refs.inputFull_2.select();
            this.refs.inputFull_2.focus();
        }
        this.onChange();
    }

    handleInputFull_2Change(e) {
        if (e.target.value.length === 2) {
            this.refs.inputFull_3.select();
            this.refs.inputFull_3.focus();
        }
        this.onChange();
    }

    render() {
        const {part, isCCRMode, lastFour, classes = {}, className = ''} = this.props;
        const {first, middle, last} = this.state;
        const cls = classNames(className, classes.root);

        let fields = null;
        if (part === 'last4') {
            fields = (
                <div>
                    <span>XXX-XX-</span>
                    <input
                        ref="inputLast4"
                        placeholder="XXXX"
                        className={classes.last4}
                        type="tel"
                        maxLength="4"
                        onChange={this.onChange}
                        value={first} />
                </div>
            );
        } else if (part === 'first2') {
            const placeholder = isCCRMode && lastFour ? `X-XX-${lastFour}` : 'X-XX-XXXX';

            fields = (
                <div>
                    <input
                        ref="inputFirst2"
                        placeholder="XX"
                        className={classes.first2}
                        type="tel"
                        maxLength="2"
                        onChange={this.onChange}
                        value={first} />
                    <span>{placeholder}</span>
                </div>
            );
        } else if (part === 'first4') {
            fields = (
                <div>
                    <input
                        ref="inputFirst4_1"
                        placeholder="XXX"
                        className={classes.first41}
                        type="tel"
                        maxLength="3"
                        onChange={this.handleInputFirst4_1Change}
                        value={first} />
                    <span>-</span>
                    <input
                        ref="inputFirst4_2"
                        placeholder="X"
                        className={classes.first42}
                        type="tel"
                        maxLength="1"
                        value={middle}
                        onChange={this.onChange} />
                    <span>X-XXXX</span>
                </div>
            );
        } else if (part === 'full') {
            fields = (
                <div>
                    <input
                        ref="inputFull_1"
                        placeholder="XXX"
                        className={classes.full1}
                        type="tel"
                        maxLength="3"
                        onChange={this.handleInputFull_1Change}
                        value={first} />
                    <span>-</span>
                    <input
                        ref="inputFull_2"
                        placeholder="XX"
                        className={classes.full2}
                        type="tel"
                        maxLength="2"
                        onChange={this.handleInputFull_2Change}
                        value={middle} />
                    <span>-</span>
                    <input
                        ref="inputFull_3"
                        placeholder="XXXX"
                        className={classes.full3}
                        type="tel"
                        maxLength="4"
                        onChange={this.onChange}
                        value={last} />
                </div>
            );
        }

        return (
            <div className={cls} onClick={this.handleClick}>
                {fields}
            </div>
        );
    }
}

const mapStateToProps = () => {
    return {
        isCCRMode: SessionService.isCCRMode()
    };
};

export default connect(mapStateToProps)(injectSheet(styles)(SSNInput));
